import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Menu,
  List,
  ListItem,
  ListSubheader,
  SxProps,
  Box,
  Slide,
  Stack,
  Typography,
  useTheme,
  alpha,
  PopoverOrigin,
} from "@mui/material";
import { Button } from "@repo/ui";
import { hideScrollbar } from "../../../theme/mixins";
import { navHover, navSelected } from "../../../theme/navChrome";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import {
  Preview,
  OwnerPreview,
  ProductPreview,
  CompanyPreview,
  OrderPreview,
  PaymentCell,
  AddressCell,
} from "components";
import { Search } from "../Search/Search";
import { getValuesByKeys } from "../../../utils/array";
import {
  resolveFontFamily,
  useDynamicFontsLoader,
} from "../../../hooks/useDynamicFontLoader";

interface MenuButtonProps {
  items: MenuButtonItem[];
  variant?: "secondary" | "primary" | "tertiary" | "text";
  transformOrigin?: PopoverOrigin;
  anchorOrigin?: PopoverOrigin;
  onSelect?: any;
  sx?: Record<string, string>;
  selected?: {
    label: string;
    value: any;
  };
  search?: boolean;
  buttonProps?: object;
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  button?: any;
  menuBottomButton?: any;
  sortRef?: any;
  openEvent?: any;
  searchParams?:
    | {
        fields: string[];
        preview: string;
      }
    | false;
  limit?: number;
  selectedItemFontFamily?: string;
  isInfiniteScroll?: boolean;
  searchSx?: Record<string, string>;
  // Compact menu: tighter top padding + shorter rows, for small pickers
  // (e.g. rows-per-page). Opt-in — default keeps the roomy 48px rows.
  dense?: boolean;
  // REMOVED: `brandHover`. Hover is neutral for every menu now — see the note on the
  // row's `&:hover` below and navChrome.ts. Left as a comment so the next person to
  // want a purple hover finds the argument instead of re-adding the flag.
  // Explicit min-width (px) for the dropdown menu. Opt-in; default keeps the
  // original 180px so Sort and other Selects are unchanged.
  menuMinWidth?: number;
  /**
   * The menu paper's corner radius in px. Defaults to **8** — platform's current
   * value, so every existing caller is pixel-unchanged.
   *
   * A prop rather than a constant because the two apps genuinely disagree and
   * neither is wrong: `apps/platform`'s dropdowns are 8, while `apps/app`'s own
   * desktop dropdown (`components/UI/ActionMenu`) is 12. A sort menu opening in
   * the same toolbar row as a 12px `ActionMenu` with an 8px corner reads as a
   * different component — which, since this file replaced that hand-roll, it no
   * longer is.
   *
   * Found by MERGING, which is the part worth recording: `origin/main` had
   * independently set this menu's paper to 12 during a chrome sweep, and adopting
   * the shared component silently reverted it to 8. Parameterising is §47's
   * answer (improve layer 1) rather than the two alternatives — fork the
   * component again, or move platform's pixels to suit the other app.
   *
   * ABSOLUTE PX like every other dimension here; see the paper `sx` note below.
   */
  menuRadius?: number;
  // Copy shown when there are no items (default "No results"). Override e.g.
  // "No saved views yet" for non-search lists.
  emptyText?: string;
  /**
   * CONTROLLED MODE — pass `open` and the component stops owning its own open
   * state and stops rendering a trigger at all; the caller supplies both, and
   * `anchorEl` is the element to hang the popover off.
   *
   * Why it exists: the menu and the button that opens it are not always in the
   * same component. `apps/app`'s certificates toolbar owns its Sort button
   * (badge, adjacent clear-X) in `HeaderBar` and renders the field list from a
   * sibling, so it can hand this component an anchor but cannot hand it a
   * child to wrap. Without a controlled mode the only way to reuse this list
   * was to copy it, which is how the two apps' menus drifted in the first
   * place.
   *
   * Uncontrolled (`open === undefined`) is unchanged in every respect, so no
   * existing call site is affected.
   */
  open?: boolean;
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
}

export interface MenuButtonItem {
  label: string;
  value: any;
  style?: object & { fontFamily?: string };
  // Optional leading icon (e.g. a brand glyph) rendered before the label.
  icon?: React.ReactNode;
  // Optional category — when set, items are visually grouped under a subheader
  // (pass items pre-sorted by group). Items without a group render flat.
  group?: string;
  // Optional trailing node rendered at the row's end (before the selected check),
  // e.g. a delete IconButton. Its own onClick should stopPropagation so it doesn't
  // trigger row selection.
  trailing?: React.ReactNode;
}

export const MenuButton = (props: MenuButtonProps) => {
  const {
    items = [],
    sx = {},
    search = false,
    selected = { label: "", value: null, style: null }, // TODO: change this prop to value
    onSelect,
    sortRef,
    openEvent = false,
    searchParams = false,
    menuBottomButton = false,
    button = false, // custom button
    buttonProps = {
      size: "medium",
      variant: "secondary",
      fullWidth: false,
      sx: {},
    },
    size = "medium", // TODO: combine these into a ButtonProps prop instead
    fullWidth = false,
    variant = "secondary",
    transformOrigin = { horizontal: "right", vertical: "top" },
    anchorOrigin = { horizontal: "right", vertical: "bottom" },
    limit,
    selectedItemFontFamily,
    isInfiniteScroll = false,
    searchSx = {},
    dense = false,
    menuMinWidth,
    menuRadius = 8,
    emptyText = "No results",
    open: openProp,
    anchorEl: anchorElProp,
    onClose: onCloseProp,
  } = props;
  const anchorEl = useRef<HTMLDivElement>(null);
  const isControlled = openProp !== undefined;

  // A drop-UP menu (transformOrigin bottom) is positioned by its BOTTOM edge, so MUI
  // computes the paper's `top` as (anchor top - paper height). Margin-bottom can't move
  // an absolutely-positioned paper that only has `top` set, and a POSITIVE margin-top
  // shifts it DOWN — straight over the trigger. It needs a negative one to lift clear.
  const isDropUp = transformOrigin?.vertical === "bottom";

  const [localLimit, setLocalLimit] = useState(limit || null);
  const [openState, setOpenState] = useState(false);
  const open = isControlled ? (openProp as boolean) : openState;
  const menuAnchor = isControlled ? (anchorElProp ?? null) : anchorEl.current;
  const [searchValue, setSearchValue] = useState("");
  const [selectedItem, setSelectedItem] = useState(selected);
  const theme = useTheme();

  /**
   * Whether the menu's list has been scrolled under the sticky search box.
   *
   * Drives only the search box's bottom rule (see its `sx`): a divider is meaningful
   * once content is passing beneath it, and is noise before that.
   *
   * The handler is wired straight onto the scrolling element via
   * `slotProps.paper.onScroll`, NOT an effect that finds the scrollport and calls
   * `addEventListener`. That was the first attempt and it silently never armed —
   * instrumenting `addEventListener` while opening the menu recorded zero `scroll`
   * registrations, because the effect ran against a ref on a child of MUI's portalled
   * paper and bailed before it got that far. Handing the prop to the element that
   * actually scrolls removes the ref, the walk-up, the bind and the teardown, and there
   * is no longer anything to get wrong.
   *
   * Note `scroll` does not bubble, so React attaches this directly to the paper — which
   * is exactly the element whose `scrollTop` is being read.
   */
  const [searchScrolled, setSearchScrolled] = useState(false);

  // A menu reopened after being left scrolled would otherwise show the rule over an
  // unscrolled list — the paper unmounts on close, but this state does not.
  useEffect(() => {
    if (!open) setSearchScrolled(false);
  }, [open]);

  const dropdownFullWidth = useMemo(() => {
    return anchorEl.current?.clientWidth
      ? anchorEl.current.clientWidth - 30
      : 80;
  }, [anchorEl.current?.clientWidth]);

  const listHeight = useMemo(() => {
    return sx?.height ? `${Number(sx.height.split("px")[0]) - 170}px` : "auto";
  }, []);

  const handleClose = () => {
    if (isControlled) onCloseProp?.();
    else setOpenState(false);
  };

  const onHandleSelect = (value: any) => {
    handleClose();
    onSelect(value);
    setSelectedItem(value);
  };

  const handleClick = () => {
    if (!isControlled) setOpenState(true);
  };

  const onSearch = (value: string) => {
    setSearchValue(value);
  };

  const searchItems = useMemo(() => {
    if (searchParams) {
      const filteredItems = items.filter((item) =>
        JSON.stringify(getValuesByKeys(item, searchParams?.fields))
          .toLowerCase()
          .includes(searchValue)
      );

      if (localLimit) {
        return filteredItems.slice(0, localLimit);
      } else {
        return filteredItems;
      }
    } else {
      const filteredItems = items.filter((item) =>
        item.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      if (localLimit) {
        return filteredItems.slice(0, localLimit);
      } else {
        return filteredItems;
      }
    }
  }, [localLimit, searchParams, searchValue, items]);

  useEffect(() => {
    setLocalLimit(limit);
  }, [searchValue]);

  const fontNames = useMemo(() => {
    if (!open) return [selectedItemFontFamily];
    if (!searchItems) return [];

    return searchItems
      .map((item) => {
        if (
          item.style &&
          typeof item.style === "object" &&
          "fontFamily" in item.style
        ) {
          return item.style.fontFamily as string;
        }
        return null;
      })
      .filter((font): font is string => font !== null);
  }, [open, searchItems]);

  useDynamicFontsLoader(fontNames);

  // TODO: grow to fit label smoothly
  // console.log(selected, items)
  return (
    <div ref={anchorEl}>
      {/* Controlled mode: the caller owns the trigger, so render none. */}
      {isControlled ? null : button ? (
        <Box onClick={handleClick}>{button}</Box>
      ) : (
        <Button
          onClick={handleClick}
          variant={variant}
          size={size}
          sx={{
            // borderRadius: 2,
            // fontFamily: selectedItem?.style?.fontFamily,
            // display: 'flex',
            minWidth: fullWidth && "100%",
            justifyContent: "space-between",
            ...buttonProps?.sx,
          }}
          endIcon={
            open ? (
              <ChevronUpIcon strokeWidth={1} />
            ) : (
              <ChevronDownIcon strokeWidth={1} />
            )
          }
        >
          <Typography
            sx={{
              // Defaults FIRST, caller's sx last — spreading `buttonProps.sx`
              // ahead of these silently discarded any `fontSize` a caller passed
              // (e.g. RowsPerPageSelector), since the literal below always won.
              fontSize: 16,
              // RESOLVED, not raw. A font value may be a URL (an uploaded brand
              // font) as well as a family name — the loader above needs the URL
              // to fetch, but a URL is not a legal `font-family`, so the two
              // uses diverge here. See `useDynamicFontLoader`.
              fontFamily: resolveFontFamily(selectedItemFontFamily),
              ...buttonProps?.sx,
            }}
          >
            {selectedItem.label}
          </Typography>
        </Button>
      )}
      <Menu
        anchorEl={menuAnchor}
        transformOrigin={transformOrigin}
        anchorOrigin={anchorOrigin}
        open={open}
        variant="menu"
        onClose={handleClose}
        sx={{
          mt: 0,
          width: fullWidth ? `${dropdownFullWidth}px` : "auto",
          maxHeight: `calc(60vh)`,
          ...sx,
        }}
        MenuListProps={{
          sx: {
            // Vertical inset is owned by the paper (below) so the gap between the
            // menu border and the items is uniform on all four sides.
            pb: 0,
            pt: 0,
            borderRadius: `${menuRadius}px`,
            minWidth: fullWidth ? `${dropdownFullWidth}px` : (menuMinWidth ?? 180),
          },
        }}
        slotProps={{
          paper: {
            // The paper IS the scrollport (the MenuList inside it is `overflow: visible`),
            // so this is the element whose scrollTop the sticky search box's rule depends
            // on. See `searchScrolled`.
            onScroll: (e: React.UIEvent<HTMLDivElement>) =>
              setSearchScrolled(e.currentTarget.scrollTop > 0),
            sx: {
              // ⚠️ ABSOLUTE PX THROUGHOUT THIS COMPONENT'S MENU GEOMETRY, and it
              // has to stay that way — see working-conventions §47. A layer-1
              // component renders under whichever app's theme mounts it, and the
              // two themes disagree on BOTH multipliers this file used to lean on:
              //
              //                    @repo/ui (platform)   apps/app
              //   spacing base            4px              8px   (MUI default)
              //   shape.borderRadius        4               12
              //
              // So the old `pl: 2` was 8px in platform and 16px in apps/app, and
              // `borderRadius: 2` was 8px against 24px. Unlike a per-call-site
              // `spacing={n}` there is no "convert per app" available from inside
              // the component, so the factors are resolved here instead. Every
              // value below is the SAME COMPUTED PIXEL platform already rendered
              // (4 x the old factor) — this is a no-op there and a fix everywhere
              // else. Do not reintroduce a bare number.
              //
              // Gap between the trigger and the popover menu. 12px — the app's
              // canonical small gap (§29), not an arbitrary bump: measured at 8px
              // this read as the menu sitting ON the control rather than below it,
              // because at a ~880px-wide trigger 8px is under 1% of the width and
              // the eye needs a clearer break to separate two stacked surfaces of
              // near-identical fill. Sign flips for a drop-up — see `isDropUp`.
              mt: isDropUp ? "-12px" : "12px",
              minWidth: menuMinWidth,
              // Uniform inset on all four sides — equal top/bottom and left/right
              // gap so the highlighted row nests concentrically inside the menu.
              pl: dense ? "4px" : "8px",
              pr: dense ? "4px" : "8px",
              // Slightly tighter top inset (was 8px) — the search box / first row
              // sat a touch too far below the menu's top edge.
              pt: dense ? "4px" : "6px",
              pb: menuBottomButton ? "8px" : dense ? "4px" : "8px",
              borderRadius: `${menuRadius}px`,
              // Flat, theme-aware surface: kill MUI's dark elevation overlay
              // (the "weird gray background") and the gray glow shadow; use a
              // clean divider border + a mode-aware shadow instead.
              backgroundColor: "background.paper",
              backgroundImage: "none",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 3,
            },
          },
        }}
      >
        {search && (
          <Stack
            sx={{
              position: "sticky",
              // A full BLEED over the paper's own padding, so rows cannot show in the
              // strip above the search box.
              //
              // ⚠️ `top` IS NEGATIVE, AND IT HAS TO BE. A previous fix added the negative
              // margins below and left `top: 0`, which looks right and does not work: a
              // sticky offset is measured from the scrollport's PADDING box, so `top: 0`
              // re-pins the element 6px down — exactly undoing the bleed the moment the
              // list scrolls. Measured with the menu open and scrolled: the paper's top
              // edge sat at y=151 and the stuck box at y=158, and two rows were painting
              // in the 7px gap between them. Matching `top` to the paper's own `pt`
              // pulls the stuck position back up to the border box.
              top: dense ? "-4px" : "-6px",
              mt: dense ? "-4px" : "-6px",
              mx: dense ? "-4px" : "-8px",
              pt: dense ? "4px" : "6px",
              px: dense ? "4px" : "8px",
              zIndex: 2,
              // Breathing room between the search box and the first row so the
              // list isn't compressed against it.
              pb: "12px",
              backgroundColor: theme.palette.background.paper,
              // A rule that appears ONLY once the list is scrolled under the box. At rest
              // there is nothing to divide — the search is simply the top of the menu —
              // and a permanent line there reads as a header the menu does not have.
              //
              // The border is always present and only changes COLOUR, because toggling
              // `borderBottom` itself would add 1px on scroll and nudge every row down.
              //
              // The resolved `theme.palette.divider` rather than the `"divider"` token, so
              // this does not depend on whether `sx` palette-maps the per-side longhand
              // `borderBottomColor` the way it does `borderColor`. (Not isolated either
              // way — the token may well work; the explicit value removes the question.)
              borderBottom: "1px solid",
              borderBottomColor: searchScrolled
                ? theme.palette.divider
                : "transparent",
              transition: "border-bottom-color 120ms linear",
            }}
          >
            <Search
              onChange={(value) => onSearch(value)}
              // FULL WIDTH of the menu. `Search` defaults to `minWidth: 300` with no
              // width, which is right for a toolbar where it sits beside other
              // controls and wrong here, where it is the only thing in the row: in
              // any menu wider than 300px the box stopped at 300 and left a ragged
              // gap to the right edge, while every row below it ran the full width.
              // `minWidth: 0` has to come first or the 300 floor wins over the 100%.
              sx={{ minWidth: 0, width: "100%", ...searchSx }}
            />
          </Stack>
        )}
        <List
          disablePadding
          sx={{
            width: "100%",
            // Top padding so the first row — including a selected row's brand
            // highlight — clears the sticky search and isn't clipped by the
            // overflow:auto edge (4px was too tight; the highlight got cut off).
            pt: "6px",
            height: listHeight,
            maxHeight: `calc(60vh)`,
            overflowY: "auto",
            bgcolor: "background.paper",
            ...hideScrollbar,
          }}
          onScroll={(e) => {
            const target = e.target as HTMLElement;
            const { scrollTop, scrollHeight, clientHeight } = target;
            const distanceFromBottom =
              scrollHeight - (scrollTop + clientHeight);

            if (distanceFromBottom < 20 && limit && isInfiniteScroll) {
              setLocalLimit((prev) => (prev ? prev + limit : limit));
            }
          }}
        >
          {searchItems.length == 0 ? (
            <Stack
              justifyContent="center"
              alignItems="left"
              pb="8px"
              pt="8px"
              pl="8px"
            >
              <Typography variant="body3" color="text.secondary">
                {emptyText}
              </Typography>
            </Stack>
          ) : (
            <>
              {searchItems.map((item, index) => {
                const prevGroup = index > 0 ? searchItems[index - 1].group : undefined;
                const showGroupHeader = !!item.group && item.group !== prevGroup;
                return (
                  <React.Fragment key={`menu-${index}`}>
                  {showGroupHeader && (
                    <ListSubheader
                      disableSticky
                      sx={{
                        bgcolor: "transparent",
                        color: "text.disabled",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        lineHeight: "18px",
                        px: "12px",
                        mt: index === 0 ? 0 : "8px",
                        pt: index === 0 ? "4px" : "8px",
                        pb: "2px",
                        borderTop:
                          index === 0
                            ? "none"
                            : `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {item.group}
                    </ListSubheader>
                  )}
                  <ListItem
                    onClick={() => onHandleSelect(item)}
                    selected={item.value === selected?.value}
                    // A `ListItem` is not a `MenuItem`: it renders a plain <li>
                    // with no tabIndex, so before this the rows could not be
                    // reached or activated from the keyboard at all — the whole
                    // menu was pointer-only. `role="menuitem"` + tabIndex + the
                    // Enter/Space handler is the same treatment the app-side
                    // pickers already use for their `role="button"` rows.
                    role="menuitem"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onHandleSelect(item);
                      }
                    }}
                    sx={{
                      p: dense ? "6px" : "12px",
                      // Concentric nesting: the item highlight is inset 8px
                      // (paper pl/pr) inside the 8px-radius menu, so an equal
                      // 8px radius reads as *rounder* than the menu corner. A
                      // slightly smaller radius nests cleanly inside it.
                      borderRadius: "6px",
                      mb: dense ? "2px" : "4px",
                      // Last row carries no bottom margin so the paper's bottom
                      // inset matches the top (uniform gap, no doubled space).
                      "&:last-of-type": { mb: 0 },
                      minHeight: dense ? 34 : 48,
                      height: dense ? 34 : 48,
                      justifyContent: "space-between",
                      fontFamily: item?.style?.fontFamily,
                      // Compare by `value`, not JSON.stringify(item): an item may carry a
                      // React-element `icon`, which has a circular emotion ref and throws
                      // "Converting circular structure to JSON".
                      backgroundColor:
                        item.value === selected?.value
                          ? navSelected(theme)
                          : "transparent",
                      transition: "background-color 0.15s ease",
                      // The MUI `selected` prop adds `.Mui-selected` (a grey
                      // action.selected bg) that otherwise wins over the line above —
                      // override it so the active row reads brand-purple, not grey.
                      "&.Mui-selected, &.Mui-selected:hover": {
                        backgroundColor: navSelected(theme),
                      },
                      // HOVER IS NEUTRAL, always. This used to be brand @12% behind an
                      // opt-in `brandHover` flag that the table-toolbar dropdowns (Sort,
                      // Views, Download) all set, so the menus a user touches most were
                      // the purple ones.
                      //
                      // navChrome.ts is the single source that settled this and it argues
                      // the opposite: hue marks the persistent, single-instance state
                      // (selected), luminance marks the transient one (hover). Hover fires
                      // on every pointer movement and CO-OCCURS — a table row, a nav row
                      // and a menu item can all be hovered at once, giving three
                      // simultaneous purple regions. Measured, brand tint at these amounts
                      // is a ΔE ~10-20 event against a JND of ~2.3, which is defensible for
                      // the one row that says "you are here" and not for a state that
                      // follows the cursor.
                      //
                      // Selected likewise moves to navSelected (light 12% / dark 20%) from
                      // a flat 16% in both modes, so a menu row and a sidebar row are now
                      // the same colour in the same state rather than nearly the same.
                      "&:hover": {
                        backgroundColor: navHover(theme),
                      },
                      // Pinned to the SAME neutral fill as hover. The rows are
                      // keyboard-reachable now (tabIndex above) and a plain <li>
                      // has no MUI focus style, so without this a keyboard user
                      // gets no indication of which row they are on.
                      "&:focus-visible": {
                        outline: "none",
                        backgroundColor: navHover(theme),
                      },
                      "& p": {
                        fontFamily: item?.style?.fontFamily,
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" sx={{ gap: "12px" }}>
                      {searchParams?.preview == "owners" && (
                        <OwnerPreview size="medium" owner={item} />
                      )}
                      {searchParams?.preview == "products" && (
                        <ProductPreview size="medium" product={item} />
                      )}
                      {searchParams?.preview == "companies" && (
                        <CompanyPreview size="medium" owner={item} />
                      )}
                      {searchParams?.preview == "orders" && (
                        <OrderPreview size="medium" product={item} />
                      )}
                      {searchParams?.preview == "payments" && (
                        <PaymentCell paymentMethod={item} link={false} />
                      )}
                      {searchParams?.preview == "addressess" && (
                        <AddressCell address={item} link={false} />
                      )}
                      {searchParams && searchParams?.preview == undefined && (
                        <Preview
                          size="medium"
                          item={item[searchParams.fields[0]]}
                        />
                      )}
                      {!searchParams && (
                        <Stack direction="row" alignItems="center" sx={{ gap: "12px" }}>
                          {item?.icon}
                          <Typography
                            sx={{
                              fontSize: dense ? 14 : 16,
                              ...item?.style,
                              // Same split as the button label above: load by
                              // URL, render by resolved family.
                              ...(item?.style?.fontFamily
                                ? {
                                    fontFamily: resolveFontFamily(
                                      item.style.fontFamily,
                                    ),
                                  }
                                : {}),
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Stack direction="row" alignItems="center" sx={{ gap: "4px" }}>
                      {item.trailing}
                      {item.value === selected?.value && (
                        <CheckIcon strokeWidth={1} />
                      )}
                    </Stack>
                  </ListItem>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </List>
        {menuBottomButton && <Stack pt="4px">{menuBottomButton}</Stack>}
      </Menu>
    </div>
  );
};
